import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import styled from 'styled-components';

interface UpdateNotificationModalProps {
    show: boolean;
    onRefresh: () => void;
    onDismiss?: () => void;
}

const StyledModal = styled(Modal)`
    .modal-content {
        border-radius: 12px;
        border: 2px solid #007bff;
    }

    .modal-header {
        background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
        color: white;
        border-top-left-radius: 10px;
        border-top-right-radius: 10px;
        border-bottom: none;
    }

    .modal-title {
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .modal-body {
        padding: 2rem;
    }

    .modal-footer {
        border-top: 1px solid #e0e0e0;
        padding: 1rem 2rem;
    }
`;

const UpdateIcon = styled.div`
    width: 32px;
    height: 32px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
`;

const UpdateMessage = styled.div`
    font-size: 1rem;
    line-height: 1.6;
    color: #333;
    margin-bottom: 1rem;

    strong {
        color: #007bff;
    }
`;

const FeatureList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 1rem 0;

    li {
        padding: 0.5rem 0;
        display: flex;
        align-items: center;
        gap: 10px;

        &:before {
            content: '✨';
            font-size: 1.2rem;
        }
    }
`;

const RefreshButton = styled(Button)`
    background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
    border: none;
    padding: 0.75rem 2rem;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 123, 255, 0.4);
        background: linear-gradient(135deg, #0056b3 0%, #004085 100%);
    }

    &:active {
        transform: translateY(0);
    }
`;

const DismissButton = styled(Button)`
    padding: 0.75rem 1.5rem;
    font-weight: 500;
`;

export const UpdateNotificationModal: React.FC<UpdateNotificationModalProps> = ({
    show,
    onRefresh,
    onDismiss,
}) => {
    return (
        <StyledModal
            show={show}
            onHide={onDismiss}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
        >
            <Modal.Header>
                <Modal.Title>
                    <UpdateIcon>🚀</UpdateIcon>
                    New Update Available!
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <UpdateMessage>
                    <strong>Great news!</strong> A new version of OntoWorks has been deployed with
                    improvements and bug fixes.
                </UpdateMessage>
                <FeatureList>
                    <li>Performance improvements</li>
                    <li>Bug fixes and stability enhancements</li>
                    <li>New features and improvements</li>
                </FeatureList>
                <UpdateMessage>
                    Click <strong>Refresh Now</strong> to get the latest version and enjoy the best
                    experience.
                </UpdateMessage>
            </Modal.Body>
            <Modal.Footer>
                {onDismiss && (
                    <DismissButton variant="outline-secondary" onClick={onDismiss}>
                        Later
                    </DismissButton>
                )}
                <RefreshButton onClick={onRefresh}>Refresh Now</RefreshButton>
            </Modal.Footer>
        </StyledModal>
    );
};
